import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { findPost, POSTS } from "@/data/blog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowLeft, Phone, ArrowRight } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);

  useEffect(() => {
    if (post) document.title = `${post.title} — Providence Partners`;
  }, [post]);

  // Sanitize HTML before injection — blocks XSS even though our content is in-repo today.
  // Restrict to a strict tag/attribute allowlist (no scripts, no event handlers, no inline styles).
  const safeBody = useMemo(() => {
    if (!post) return "";
    return DOMPurify.sanitize(post.body, {
      ALLOWED_TAGS: ["p", "h2", "h3", "ul", "ol", "li", "strong", "em", "a", "br"],
      ALLOWED_ATTR: ["href", "target", "rel"],
    });
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="blog-post-page" className="bg-background">
      <article className="mx-auto max-w-3xl px-6 py-14 lg:px-8">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="blog-back-link">
          <ArrowLeft className="h-3 w-3" /> All caregiver tips
        </Link>

        <div className="mt-6 flex flex-wrap gap-2">
          {post.tags.map((t) => (
            <Badge key={t} variant="outline" className="rounded-full border-emerald/30 bg-emerald/5 text-emerald text-xs">{t}</Badge>
          ))}
        </div>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-forest sm:text-5xl">{post.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          <span className="mx-2">·</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readMinutes} min read</span>
          <span className="mx-2">·</span>
          <span>{post.author}</span>
        </p>

        <div
          className="prose prose-lg mt-8 max-w-none text-foreground
            [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:text-forest [&_h2]:mt-8 [&_h2]:mb-3
            [&_p]:my-3 [&_p]:leading-relaxed [&_p]:text-base [&_p]:text-foreground
            [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1 [&_li]:text-foreground
            [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6
            [&_a]:text-primary [&_a]:underline hover:[&_a]:text-emerald
            [&_strong]:text-foreground [&_strong]:font-semibold"
          data-testid="blog-post-body"
          dangerouslySetInnerHTML={{ __html: safeBody }}
        />

        <Card className="mt-12 rounded-2xl border border-gold/40 bg-gold/5 p-6 text-center">
          <p className="font-serif text-2xl text-forest">Talk to a Providence Partners advisor</p>
          <p className="mt-1 text-sm text-muted-foreground">Always available — 24/7</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full bg-primary px-6 py-5 text-base text-primary-foreground" data-testid="blog-post-call-btn">
              <a href="tel:+15863068449"><Phone className="mr-2 h-4 w-4" /> (586) 306-8449</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-6 py-5 text-base">
              <Link to="/placement-help">Request placement help <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </Card>

        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="font-serif text-xl text-forest">More for families seeking care</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Card key={r.slug} className="rounded-2xl border border-border/60 bg-card p-5">
                  <Link to={`/blog/${r.slug}`} className="font-serif text-lg text-forest hover:text-primary">{r.title}</Link>
                  <p className="mt-2 text-sm text-muted-foreground">{r.excerpt}</p>
                </Card>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
